<template>
  <div
    id="global-nav-dropdown"
    :class="isSimpleHeader ? 'top-[47px]' : 'bottom-80'"
    tabindex="-1"
    role="listbox"
    aria-label="Primary navigation menu"
  >
    <div class="flex">
      <Avatar :name="`${firstName} ${lastName}`" :imageUrl="profileImage" size="large" />
      <div class="flex flex-col ml-16">
        <p class="text-h4 truncate-name">{{ formattedName }}</p>
        <a v-if="accountType === 'Basic'" class="text-body text-primary" href="/upgrade/pricing">Upgrade to Premium</a>
        <p v-else class="text-body">{{ memberTypeText }}</p>
      </div>
    </div>
    <MenuLinkList
      :items="dropdownMenuItems"
      list-class="mt-16 border-t-1 border-solid border-grey-divider-subdued text-body text-grey-clickable list-none p-0 m-0"
      icon-color="#4B4C52"
    />
  </div>
</template>

<script lang="ts">
import type { MenuItem } from '@/components/menu_link_list/MenuLinkListTypes';
import MenuLinkList from '@/components/menu_link_list/MenuLinkList.vue';
import Avatar from '../avatar/Avatar.vue';
import ProfileIcon from '@/icons/global_nav/ProfileIcon.vue';
import FavoritesIcon from '@/icons/global_nav/FavoritesIcon.vue';
import HelpIcon from '@/icons/global_nav/HelpIcon.vue';
import LogOutIcon from '@/icons/global_nav/LogOutIcon.vue';
import PremiumUpgradeIcon from '@/icons/global_nav/PremiumUpgradeIcon.vue';
import TrustAndSafetyIcon from '@/graphics/icons/TrustAndSafetyIcon.vue';

export default {
  name: 'GlobalNavDropdown',
  components: {
    MenuLinkList,
    Avatar,
  },
  props: {
    firstName: {
      type: String,
      default: '',
    },
    lastName: {
      type: String,
      default: '',
    },
    profileImage: {
      type: String,
      default: '',
    },
    accountType: {
      type: String,
      default: '',
    },
    isSimpleHeader: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    formattedName(): string {
      return `${this.firstName} ${this.lastName ? `${this.lastName[0].toUpperCase()}.` : ''}`;
    },
    memberTypeText(): string {
      if (this.accountType === ('Basic' as String)) return 'Upgrade to Premium';
      return `${this.accountType} member`;
    },
    dropdownMenuItems(): MenuItem[] {
      return [
        {
          icon: ProfileIcon,
          label: 'Account',
          href: '/account/settings',
          visible: !this.isSimpleHeader,
        },
        {
          icon: PremiumUpgradeIcon,
          label: 'Premium pricing',
          href: '/upgrade/pricing',
          visible: this.accountType === 'Basic' && !this.isSimpleHeader,
        },
        {
          icon: FavoritesIcon,
          label: 'Favorites',
          href: '/account/seekers/favorites',
          visible: !this.isSimpleHeader,
        },
        {
          icon: TrustAndSafetyIcon,
          label: 'Trust & Safety',
          href: 'https://www.sittercity.com/trust-and-safety',
          target: '_blank',
          visible: true,
        },
        {
          icon: HelpIcon,
          label: 'Help',
          href: 'https://support.sittercity.com/hc/en-us',
          target: '_blank',
          visible: true,
        },
        {
          icon: LogOutIcon,
          label: 'Log out',
          href: '/logout',
          visible: true,
        },
      ];
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.$emit('handleClickOutside', event.target);
      }
    },
  },
};
</script>

<style lang="scss">
#global-nav-dropdown {
  padding: 1rem;
  width: 100vw;
  position: fixed;
  border-radius: 4px;
  background: theme('colors.white');
  z-index: 101;
  box-shadow: 0px 2px 12px 0px rgba(5, 7, 15, 0.12);
  @media only screen and (min-width: 768px) {
    width: 260px;
    right: 0;
    top: 80px;
    bottom: unset;
  }
  .truncate-name {
    max-width: 160px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
  }
}
</style>
